import React, { useRef, useEffect, useState } from 'react';

import startCase from 'lodash/startCase';
import { Typography, Link, Button } from '@hpi-sox/hpstellar-core/visId';
import { AIGenerate } from '@hpi-sox/hpstellar-veneer-icons';

import { withError } from '../../shared/components/error-boundary';
import useHawksearchComponent from '../useHawksearchComponent';
import { openVirtualAssistantInsideChat } from '../util';
import { useWindowSize } from '@hpi-sox/hpstellar-core/hooks';
import { BREAKPOINTS } from '@hpi-sox/hpstellar-utils';
import useInsideChatSubscription from '../../hooks/useInsideChatSubscription';

import './css/search-page-suggestion.less';

const NO_RESULTS_TEXT = "Don't worry, we are here to help. Try to use our AI Virtual Assistant.";

const SuggestionLinks = props => {
    const { suggestions, pageType } = props || {};
    const validSuggestions =
        Array.isArray(suggestions) &&
        suggestions.filter(suggestion => typeof suggestion === 'string' && suggestion.length > 0);

    return (
        Array.isArray(validSuggestions) &&
        validSuggestions.map((suggestion, idx) => {
            const href = `${process.env.BASENAME}/sitesearch?keyword=${suggestion}${pageType === 'content' ? '&type=content' : ''
                }`;
            const prefix = idx > 0 ? (idx === validSuggestions.length - 1 ? ' or ' : ', ') : '';
            return (
                <React.Fragment key={`${idx}-${suggestion}-${pageType}`}>
                    {prefix}
                    <Link variant="tertiary" to={href} className="srp-suggestion-link">
                        {suggestion}
                    </Link>
                </React.Fragment>
            );
        })
    );
};

export const SearchPageZeroResultSuggestion = ({ keyword, suggestionText = NO_RESULTS_TEXT }) => {
    let keywordDisplayed = typeof keyword === 'string' && startCase(keyword);
    const searchKeyIsValid = keywordDisplayed && keywordDisplayed.length > 0;
    const noResultsPrefix = `Sorry, we could not find any matches ${searchKeyIsValid ? ' for' : ''}`;

    const insideChatState = useInsideChatSubscription();
    const { chatAvailable, chatLoaded } = insideChatState || {}
    const showVirtualAssistantButton = chatAvailable || chatLoaded

    return (
        <div className="srp-suggestion-section srp-support-no-results-message srp-zero-result-alignment">
            <div className="srp-suggestion-results-and-keyword srp-zero-result-message-and-button-container">
                <div className="srp-zero-result-message-container">
                    <div className="srp-suggestion-text-block srp-zero-result-text-block">
                        <Typography variant="h3" tag="h3" className="srp-zero-result-keyword-text-prefix">
                            {noResultsPrefix}
                        </Typography>
                        <Typography variant="h3" tag="h3" className="srp-zero-result-keyword-text">
                            {keywordDisplayed}
                        </Typography>
                    </div>
                    <Typography variant="p" tag="p" className="srp-suggestion-result-text-next-line">
                        {suggestionText}
                    </Typography>
                </div>
                <div className="srp-zero-result-button-container">
                    {showVirtualAssistantButton && (
                        <Button startIcon={<AIGenerate size={16} />} onClick={openVirtualAssistantInsideChat}>
                            Chat with Virtual Assistant
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default withError(
    ({ activeIndex, tabs, hasNoResults, searchKey, searchCount, srpDidYouMean, suggestionText = NO_RESULTS_TEXT }) => {
        let activeTabName = tabs && tabs[activeIndex] && tabs[activeIndex].name;
        const { innerWidth } = useWindowSize();
        const [isMounted, setIsMounted] = useState(false);
        const hintDomRef = useRef();
        const altHintDomRef = useRef();
        const hawksearchResults = useHawksearchComponent();
        const { type } = hawksearchResults || {};
        const searchResults = hawksearchResults && hawksearchResults[type];
        const { currentSearch, Pagination, DidYouMean } = searchResults || {};
        const { Keyword } = currentSearch || {};

        const isSupport = activeTabName === 'support';

        const zeroResults = searchCount === 0;
        const suggestionsAreValid =
            Array.isArray(DidYouMean) && DidYouMean.filter(s => typeof s === 'string').length > 0;
        const zeroResultsWithoutSuggestions = zeroResults && (!suggestionsAreValid || isSupport);
        let keywordDisplayed = isSupport ? searchKey : Keyword;
        keywordDisplayed = typeof keywordDisplayed === 'string' && startCase(keywordDisplayed);
        const searchKeyIsValid = keywordDisplayed && keywordDisplayed.length > 0;
        const noResultsPrefix = `No results found ${searchKeyIsValid ? ' for' : ''}`;
        let resultsMessage;
        if (zeroResults) {
            resultsMessage = (
                <>
                    <div className="srp-suggestion-text-block">
                        <Typography variant="h4" tag="h4" className="srp-suggestion-result-text">
                            {noResultsPrefix}
                        </Typography>
                        <Typography variant="h4" tag="h4" className="srp-suggestion-keyword-text">
                            {keywordDisplayed}
                        </Typography>
                    </div>
                    {zeroResultsWithoutSuggestions && (
                        <Typography
                            variant="h4"
                            tag="h4"
                            className="srp-suggestion-result-text srp-suggestion-result-text-next-line"
                        >
                            {suggestionText}
                        </Typography>
                    )}
                </>
            );
        } else {
            const searchCountText = typeof searchCount === 'number' ? searchCount : '';
            const resultText = searchCount > 1 ? 'Results' : 'Result';
            const keywordText =
                typeof keywordDisplayed === 'string' && keywordDisplayed.trim().length > 0
                    ? `${resultText} for`
                    : resultText;
            resultsMessage = (
                <>
                    <Typography
                        variant="h4"
                        tag="h4"
                        className="srp-suggestion-result-text"
                    >{`${searchCountText} ${keywordText}`}</Typography>
                    <Typography variant="h4" tag="h4" className="srp-suggestion-keyword-text">
                        {keywordDisplayed}
                    </Typography>
                </>
            );
        }
        const showingResultsText = typeof Keyword === 'string' && `Showing results for ${startCase(Keyword)}`;
        const suggestionLinkProps = {
            suggestions: DidYouMean,
            pageType: activeTabName,
        };

        const [isDoubleRow, setIsDoubleRow] = useState(false);

        const checkIfDoubleRow = (hintTopPos, altHintTopPos) => {
            if (hintTopPos !== altHintTopPos && !isDoubleRow) {
                setIsDoubleRow(true);
            } else if (hintTopPos === altHintTopPos && isDoubleRow) {
                setIsDoubleRow(false);
            }
        };

        useEffect(() => {
            if (!isMounted) {
                setIsMounted(true);
            }
        }, []);

        useEffect(() => {
            try {
                const hintTopPos = hintDomRef.current.getBoundingClientRect().top;
                const altHintTopPos = altHintDomRef.current.getBoundingClientRect().top;
                checkIfDoubleRow(hintTopPos, altHintTopPos);
            } catch (e) { }
        }, [innerWidth, isMounted, !!hintDomRef, !!altHintDomRef, suggestionsAreValid]);

        const containerClassName = `srp-suggestion-section${isDoubleRow ? ' srp-hint-multi-rows' : ''}${zeroResultsWithoutSuggestions ? ` srp-support-no-results-message` : ''
            }${srpDidYouMean ? ' srp-did-you-mean' : ''}`;

        const shouldDisplaySuggestions = (suggestionsAreValid && !isSupport) || zeroResultsWithoutSuggestions;

        return (
            shouldDisplaySuggestions && (
                <div className={containerClassName}>
                    <div className="srp-suggestion-results-and-keyword">{resultsMessage}</div>
                    {!isSupport && !zeroResultsWithoutSuggestions && (
                        <div className="srp-suggestion-hints">
                            <div
                                className={`srp-suggestion-hint-section${isDoubleRow ? ' srp-hint-double-row' : ''}`}
                                ref={hintDomRef}
                            >
                                <Typography variant="body2" tag="p" className="srp-suggestion-text">
                                    Did you mean <SuggestionLinks {...suggestionLinkProps} />?
                                </Typography>
                            </div>
                        </div>
                    )}
                </div>
            )
        );
    },
);
